import { ref, useContext, useFetch } from '@nuxtjs/composition-api';
import { uniq } from 'lodash-es';
import { useCancellable } from '~/composables/useCancellable';
import { useStore } from '~/composables/useStore';
import { TOP_REVIEW_COUNT } from '~/constants';
import { nonNullable, contentsSearchParams } from '~/utils';
import { perseAreasDataToJson } from '~/utils/modules/contents';
import { ECSchema } from '~/types/api';
import { ArticleCard, EditorProfile, NewsCard, ProducerCard } from '~/types/modules/contents';
import { StaffReview } from '~/types/modules/staff';
import {
  FreeAreaData,
  NewsItemOfSlider,
  TopSlider,
  TopGourmetSnack,
  TopBottomBanner,
  TopItemDetail,
  TopSaleBanner,
  TopDistribution, // TODO:頒布会のLPがFIXしたら削除
} from '~/types/modules/top';
import { ArticleType, BooleanLike } from '~/enums';

type UnwrapPromise<T> = T extends PromiseLike<infer U> ? U : T;

const partsMapper = (result: any[][]) => {
  const pickParts = (partName: string): any[] => result.find((items) => items[0]?.article_type === partName) || [];

  return {
    partsTopSlider: pickParts('parts.top_slider'),
    partsNewsSlider: pickParts('parts.news_slider'),
    partsTopFree: pickParts('parts.top_free'),
    partsBestSellerList: pickParts('parts.bestseller_list'),
    partsWineSetList: pickParts('parts.wineset_list'),
    partsSaleBanner: pickParts('parts.sale_banner'),
    partsRecommendedFeature: pickParts('parts.recommend_feature'),
    partsBordeauxFeature: pickParts('parts.bordeaux_feature'),
    partsBourgogneFeature: pickParts('parts.bourgogne_feature'),
    partsChampagneFeature: pickParts('parts.champagne_feature'),
    partsFranceFeature: pickParts('parts.france_feature'),
    partsItalyFeature: pickParts('parts.italy_feature'),
    partsCaliforniaFeature: pickParts('parts.california_feature'),
    partsJapanFeature: pickParts('parts.japan_feature'),
    partsOtherCountriesFeature: pickParts('parts.other_countries_feature'),
    partsNewProducerFeature: pickParts('parts.new_producer_feature'),
    partsHighEndSelection: pickParts('parts.highend_sellection'),
    partsGourmetSnacks: pickParts('parts.gourmet_snacks'),
    partsFeaturedMagazine: pickParts('parts.top_magazine'),
    partsMagazine: pickParts('magazine'),
    partsBottomBanner: pickParts('parts.bottom_banner'),
    partsInformation: pickParts('information'),
    partsSubscription: pickParts('parts.subscription'), // TODO:頒布会のLPがFIXしたら削除
  };
};

// データの取得方法が特殊なのでComposableから直接APIを呼ぶ
export const useTopParts = () => {
  const { app } = useContext();
  const store = useStore();
  const cancellable = useCancellable(['fetchTopParts']);

  const fetchTopItemDetails = async <PartsName extends string>(
    topApiResponse: Record<PartsName, any[]>
  ): Promise<Record<PartsName, TopItemDetail[]>> => {
    /** ベストセラー、ワインセットの親商品コード */
    const itemCodeList = uniq(
      Object.values<any>(topApiResponse).flatMap((articles: any[]) =>
        articles.map(({ contents }) => contents.item_code)
      )
    );

    /** ハイエンドセレクションの子商品コード */
    const variantItemCodeList = uniq(
      Object.values<any>(topApiResponse).flatMap((articles: any[]) =>
        articles.map(({ contents }) => contents.variant_item_code)
      )
    );

    if (itemCodeList.length === 0) {
      // 各プロパティが空配列として返す
      return Object.fromEntries(Object.keys(topApiResponse).map((key) => [key, [] as any[]])) as Record<
        PartsName,
        TopItemDetail[]
      >;
    }

    const itemsFromSearch = await store.dispatch('modules/product/fetchTopItemCardProducts', {
      parentCodes: itemCodeList,
      variantCodes: variantItemCodeList,
      cancelToken: cancellable.token('fetchTopParts'),
    });

    const results: Record<PartsName, TopItemDetail[]> = Object.fromEntries(
      Object.entries(topApiResponse).map(([partsName, partResult]: any) => {
        const itemDetails = partResult
          .map(({ contents }: any) => {
            const productFromSearch = itemsFromSearch.find(
              (searchResponse) =>
                searchResponse.code === contents.item_code ||
                searchResponse.product_variant_code_sm.includes(contents.variant_item_code)
            );
            if (!productFromSearch) return null;

            return {
              code: contents.item_code || productFromSearch.code,
              catchCopy: contents.catch_copy,
              // ワインセットの場合は1番目の画像、それ以外はボトル画像(2番目)
              image: partsName === 'wineSetList' ? productFromSearch.image1 : productFromSearch.image2,
              product: productFromSearch,
              variantCode: contents.variant_item_code,
            };
          })
          .filter(nonNullable);

        return [partsName, itemDetails];
      })
    );

    return results;
  };

  const fetchFeatureDetailList = async <PartsName extends string>(
    topApiResponse: Record<PartsName, any[]>
  ): Promise<Record<PartsName, ArticleCard[]>> => {
    // 取得対象の記事ID
    const featureIdList = uniq(
      Object.values<any>(topApiResponse).flatMap((features: any[]) =>
        features.map(({ contents }) => contents.feature_id || contents.item_code)
      )
    );

    const TOP_FEATURE_NUM = 40; // = おすすめ特集の記事数8 + (一産地あたりの記事数4 * 産地数8)
    const { q } = contentsSearchParams({
      articleType: ArticleType.Archives,
      articleIds: featureIdList.map((featureId) => `article_${featureId.toString()}`).join(' OR '),
      shouldIncludeExpiredArticle: BooleanLike.TRUE,
    });
    const { articles } = await store.dispatch('modules/content/fetchArticles', {
      q,
      fl: [
        'iid',
        'article_permalink_s',
        'article_sp_thumbnail_s',
        'article_pc_thumbnail_s',
        'article_sp_main_image_s',
        'article_pc_main_image_s',
      ],
      limit: TOP_FEATURE_NUM,
    });

    const results: Record<PartsName, ArticleCard[]> = Object.fromEntries(
      Object.entries(topApiResponse).map(([partsName, partsResult]: any) => {
        const featureDetails = partsResult
          .map(({ contents }: any): ArticleCard | null => {
            const { feature_id, catch_copy } = contents;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const feature = articles.find(({ id }) => id.replace('article_', '') === feature_id);

            if (!feature) {
              return null;
            }

            return {
              id: feature.id,
              permalink: feature.permalink,
              title: catch_copy,
              thumbnailImageSp: feature.thumbnailImageSp,
              thumbnailImagePc: feature.thumbnailImagePc,
              mainImageSp: feature.mainImageSp,
              mainImagePc: feature.mainImagePc,
            };
          })
          .filter(nonNullable);

        return [partsName, featureDetails];
      })
    );

    return results;
  };

  const fetchNewProducerFeatures = async (topApiResponse: any[]): Promise<ProducerCard[]> => {
    const producerIdList = uniq(topApiResponse.map(({ contents }) => contents.producer_id));

    if (producerIdList.length === 0) {
      return [];
    }

    const [{ result: articles = [] }, { response: producers = [] }] = await Promise.all([
      app.$cmsAxios.$get('/view_articles', {
        query: {
          '_article_type[eq]': ArticleType.Producers,
          '_permalink[in]': `[${producerIdList}]`,
        },
      }),
      app.$searchAxios.$get('/select', {
        query: {
          wt: 'json',
          q: `mst:producer AND producer_id_i: (${producerIdList.join(' OR ')})`,
          fl: [
            'item_id_i',
            'producer_id_i',
            'producer_name_s',
            'page_title_s',
            'producer_name_english_s',
            'short_caption_t',
            'areas_s',
            'new_label_start_d',
            'new_label_end_d',
            'vintage_new_label_start_d',
            'vintage_new_label_end_d',
            'product_count_i',
            'product_image2_t',
            'main_image_pc_s',
            'main_image_sp_s',
            'thumbnail_image_pc_s',
            'thumbnail_image_sp_s',
          ],
        },
      }),
    ]);

    return (
      topApiResponse
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .map(({ contents: { producer_id, text } }): ProducerCard | null => {
          const producer = articles.find(({ permalink }) => Number(permalink) === producer_id);
          const producerMaster = producers.docs.find((producer: any) => Number(producer.producer_id_i) === producer_id);

          if (!producer) {
            return null;
          }

          const { article_id, permalink } = producer;
          const {
            producer_name_s,
            producer_name_english_s,
            page_title_s,
            short_caption_t,
            areas_s,
            new_label_start_d,
            new_label_end_d,
            vintage_new_label_start_d,
            vintage_new_label_end_d,
            product_count_i,
            product_image2_t,
            main_image_pc_s,
            main_image_sp_s,
            thumbnail_image_pc_s,
            thumbnail_image_sp_s,
          } = producerMaster;

          return {
            id: article_id,
            permalink,
            name: producer_name_s,
            nameEn: producer_name_english_s,
            thumbnailSpImage: thumbnail_image_sp_s,
            thumbnailPcImage: thumbnail_image_pc_s,
            mainSpImage: main_image_sp_s,
            mainPcImage: main_image_pc_s,
            title: page_title_s,
            catchCopy: short_caption_t,
            areas: perseAreasDataToJson(areas_s),
            topText: text,
            newLabelStart: new_label_start_d,
            newLabelEnd: new_label_end_d,
            vintageNewLabelStart: vintage_new_label_start_d,
            vintageNewLabelEnd: vintage_new_label_end_d,
            productCount: product_count_i,
            productImage2: product_image2_t,
          };
        })
        .filter(nonNullable)
    );
  };

  const fetchMagazineDetailList = async (topApiResponse: any[], excludedMagazineId: string) => {
    const authorIdList = uniq(
      topApiResponse.filter((item) => item.article_id !== excludedMagazineId).map(({ contents }) => contents.author_id)
    ).filter(nonNullable);

    const [magazineCategories, { result: authors = [] }] = await Promise.all([
      // カテゴリ一覧を取得
      store.dispatch('modules/content/fetchArticleCategories', {}),
      // 投稿者情報を取得
      app.$cmsAxios.$get('/view_articles', {
        query: {
          '_article_type[eq]': ArticleType.Authors,
          '_article_id[in]': `[${authorIdList.join(',')}]`,
        },
      }),
    ]);

    const magazine: ArticleCard[] = topApiResponse
      .filter((item) => item.article_id !== excludedMagazineId)
      .map((item) => ({
        id: item.article_id,
        permalink: item.permalink,
        thumbnailImageSp: item.contents.thumbnail_image_sp,
        thumbnailImagePc: item.contents.thumbnail_image_pc,
        mainImageSp: item.contents.main_image_sp,
        mainImagePc: item.contents.main_image_pc,
        spImage: item.contents.thumbnail_image_sp,
        pcImage: item.contents.thumbnail_image_pc,
        title: item.title,
        authorId: item.contents.author_id,
        categoryId: item.contents.category_id,
        publishedAt: item.publish_at,
      }));
    const magazineAuthors: EditorProfile[] = authors.map((item: any) => ({
      id: item.article_id,
      permalink: item.permalink,
      image: item.contents.profile_image,
      name: item.contents.author_name,
      profile: item.contents.description,
      twitter: item.contents.twitter_url,
      instagram: item.contents.instagram_url,
      facebook: item.contents.facebook_url,
    }));

    return {
      magazine,
      magazineAuthors,
      magazineCategories,
    };
  };

  const fetchFeaturedMagazineDetail = async (magazineId: string) => {
    if (!magazineId) {
      return {
        featuredMagazine: undefined,
        featuredMagazineAuthor: [],
      };
    }

    const result = await store.dispatch('modules/content/fetchArticle', {
      id: magazineId,
    });

    const featuredMagazineAuthor = result.contents.author_id
      ? await store.dispatch('modules/content/fetchAuthors', {
          authorIds: [result.contents.author_id],
        })
      : [];

    return {
      featuredMagazine: {
        id: result.article_id,
        permalink: result.permalink,
        // NOTE: TOPで表示するサムネイル画像はSPでも4:3の画角になるため、SP画像の変数にPC画像を代入する
        thumbnailImageSp: result.contents.thumbnail_image_pc,
        thumbnailImagePc: result.contents.thumbnail_image_pc,
        mainImageSp: result.contents.main_image_pc,
        mainImagePc: result.contents.main_image_pc,
        title: result.title,
        authorId: result.contents.author_id,
        categoryId: result.contents.category_id,
        publishedAt: result.publish_at,
      },
      featuredMagazineAuthor,
    };
  };

  const fetchInformationShopNames = async (information: Array<any>): Promise<Array<Array<string>>> => {
    const allDestinationIds = information.map((news) => news.contents.display_destination.slice(0, 5));
    const uniqueIds = uniq(allDestinationIds.flat());

    const { data } = await store.dispatch('modules/salesLocation/fetchAllSalesLocations', {
      query: {
        ids: uniqueIds.join(','),
        limit: uniqueIds.length,
        page: 1,
      },
    });

    const shopNames: Array<Array<string>> = Array(information.length)
      .fill([])
      .map(() => []);
    data?.forEach((shopDetail: ECSchema<'saleslocation.SalesLocation'>) => {
      allDestinationIds.forEach((destinationIds: Array<string>, index) => {
        destinationIds.forEach((destinationId) => {
          if (shopDetail.id === Number(destinationId) && shopDetail.place_local_name) {
            shopNames[index].push(shopDetail.place_local_name);
          }
        });
      });
    });

    return shopNames;
  };

  const fetchStaffReviews = async (): Promise<Array<StaffReview>> => {
    const { items } = await store
      .dispatch('modules/staff/fetchStaffReviewList', {
        cancelToken: cancellable.token('fetchTopParts'),
        count: TOP_REVIEW_COUNT,
      })
      .catch(() => ({ items: [] }));
    return items;
  };

  const fetchTag2Labels = async (): Promise<string[]> => {
    // TOP画面では表示フラグに関係なくシーン・キーワードを全て表示する
    return await store.dispatch('modules/tagList/fetchKeywordList', {}).catch(() => []);
  };

  const fetchTopParts = async () => {
    const { result } = await app.$consolsAxios.$get('/api/v1/top' as any, {
      cancelToken: cancellable.token('fetchTopParts'),
    });

    const {
      partsTopSlider,
      partsNewsSlider,
      partsTopFree,
      partsBestSellerList,
      partsWineSetList,
      partsSaleBanner,
      partsRecommendedFeature,
      partsBordeauxFeature,
      partsBourgogneFeature,
      partsChampagneFeature,
      partsFranceFeature,
      partsItalyFeature,
      partsCaliforniaFeature,
      partsJapanFeature,
      partsOtherCountriesFeature,
      partsNewProducerFeature,
      partsHighEndSelection,
      partsGourmetSnacks,
      partsFeaturedMagazine,
      partsMagazine,
      partsBottomBanner,
      partsInformation,
      partsSubscription, // TODO:頒布会のLPがFIXしたら削除
    } = partsMapper(result);

    const featuredMagazineId: string = partsFeaturedMagazine?.[0]?.contents.magazine_ids[0] || '';

    const [
      topItemDetails,
      topFeatureDetails,
      newProducerFeature,
      magazineDetails,
      featuredMagazineDetail,
      informationShopNames,
      staffReviews,
      tag2Labels,
    ] = await Promise.all([
      fetchTopItemDetails({
        // 単品枠：ベストセラー
        bestSellerList: partsBestSellerList,
        // 単品枠：ワインセット
        wineSetList: partsWineSetList,
        // ハイエンドセレクション
        highEndSelection: partsHighEndSelection,
      }),
      fetchFeatureDetailList({
        // おすすめ特集
        recommendedFeature: partsRecommendedFeature,
        // 産地特集：ボルドー
        bordeauxFeature: partsBordeauxFeature,
        // 産地特集：ブルゴーニュ
        bourgogneFeature: partsBourgogneFeature,
        // 産地特集：シャンパーニュ
        champagneFeature: partsChampagneFeature,
        // 産地特集：その他フランス
        franceFeature: partsFranceFeature,
        // 産地特集：イタリア
        italyFeature: partsItalyFeature,
        // 産地特集：カリフォルニア
        californiaFeature: partsCaliforniaFeature,
        // 産地特集：日本
        japanFeature: partsJapanFeature,
        // 産地特集：その他国・地域
        otherCountriesFeature: partsOtherCountriesFeature,
      }),
      fetchNewProducerFeatures(partsNewProducerFeature),
      fetchMagazineDetailList(partsMagazine, featuredMagazineId),
      fetchFeaturedMagazineDetail(featuredMagazineId),
      fetchInformationShopNames(partsInformation),
      fetchStaffReviews(),
      fetchTag2Labels(),
    ]);

    return {
      ...topItemDetails,
      ...topFeatureDetails,
      newProducerFeature,
      // トップスライダー
      topSlider: partsTopSlider.map(
        (item): TopSlider => ({
          id: item.article_id,
          title: item.title,
          pc_img: item.contents.pc_img,
          sp_img: item.contents.sp_img,
          url: item.contents.url,
          target: item.contents.target || false,
          display_order: item.contents.display_order || Infinity,
        })
      ),
      // ニューススライダー
      newsSlider: partsNewsSlider.map(
        (item): NewsItemOfSlider => ({
          id: item.article_id || '',
          title: item.title || '',
          category: item.contents.category || '',
          date: item.publish_at || '',
          link: item.contents.url,
          target: item.contents.target || false,
        })
      ),
      // トップ自由領域
      topFree: partsTopFree.map(
        (item): FreeAreaData => ({
          id: item.article_id || '',
          title: item.title || '',
          text: item.contents.text || '',
          link:
            item.contents.link_text?.text && item.contents.link_text?.url
              ? {
                  text: item.contents.link_text.text,
                  url: item.contents.link_text.url,
                }
              : undefined,
          target: item.contents.target || false,
        })
      ),
      // セールバナー
      saleBanner: partsSaleBanner.map(
        (item: any): TopSaleBanner => ({
          id: item.article_id || '',
          title: item.title || '',
          pc_img: item.contents.pc_img || '',
          sp_img: item.contents.sp_img || null,
          link: item.contents.url || null,
          target: item.contents.target || false,
        })
      ),
      // グルメなおつまみ
      gourmetSnacks: partsGourmetSnacks.map(
        (item: any): TopGourmetSnack => ({
          id: item.article_id || '',
          title: item.title || '',
          pc_img: item.contents.pc_img || '',
          sp_img: item.contents.sp_img || null,
          text: item.contents.text || '',
          link: item.contents.url || null,
          target: item.contents.target || false,
        })
      ),
      // ワインの読み物
      ...featuredMagazineDetail,
      ...magazineDetails,
      // ボトムバナー
      bottomBanner: partsBottomBanner.map(
        (item: any): TopBottomBanner => ({
          id: item.article_id || '',
          title: item.title || '',
          pc_img: item.contents.pc_img || '',
          sp_img: item.contents.sp_img || null,
          link: item.contents.url || null,
          target: item.contents.target || false,
        })
      ),
      // お知らせ
      information: partsInformation.map(
        (item: any): NewsCard => ({
          id: item.article_id,
          link: item.permalink,
          category: item.contents.category,
          title: item.title,
          publish_at: item.publish_at,
          display_destination: item.contents.display_destination,
        })
      ),
      // お知らせの各店舗名
      informationShopNames,
      // TODO:頒布会のLPがFIXしたら削除
      // 頒布会（定期購入）
      distribution: partsSubscription.map(
        (item: any): TopDistribution => ({
          id: item.article_id || '',
          pc_img: item.contents.pc_img || '',
          sp_img: item.contents.sp_img || null,
          text: item.contents.text || '',
          link: item.contents.url || null,
          target: item.contents.target || false,
        })
      ),
      staffReviews,
      tag2Labels,
    };
  };

  const parts = ref<UnwrapPromise<ReturnType<typeof fetchTopParts>>>();

  useFetch(async () => {
    try {
      parts.value = await fetchTopParts();
    } catch {
      // do nothing.
    }
  });

  return parts;
};
